import { Injectable, OnDestroy } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { TokenDialogComponent } from '../components/token-dialog/token-dialog.component';
import { TokenRefreshService } from './token-refresh.service';
import { setAuthToken } from '../../app/state/actions';

/** What a decoded access token says about its own expiry. */
export interface TokenExpirationInfo {
  expirationDate: Date;
  secondsRemaining: number;
  isExpired: boolean;
}

/** setTimeout treats delays beyond ~24.8 days as zero, so long waits are re-armed in chunks. */
const MAX_TIMER_MS = 2 ** 31 - 1;

/** How far ahead of expiry the token is renewed, so a request never races the deadline. */
const REFRESH_SKEW_SECONDS = 5 * 60;

/** How long to wait before retrying a renewal that failed for a transient reason. */
const RETRY_DELAY_MS = 60 * 1000;

/**
 * Keeps the session token fresh, and owns the "Token is Expired" dialog for the one case
 * that freshness cannot cover.
 *
 * `watchToken()` schedules a silent renewal shortly before the token lapses, so an idle
 * session stays signed in without the operator seeing anything. The dialog is reached only
 * when Azure AD says interaction is genuinely required — in practice once the 24-hour
 * lifetime of the SPA refresh token has run out, since redeeming it does not extend that
 * window.
 *
 * Every path that does reach the dialog funnels through `promptReauthentication()`, so an
 * unrecoverable session produces one prompt rather than an error message in whichever
 * screen happened to make the next request.
 */
@Injectable({ providedIn: 'root' })
export class TokenExpiryService implements OnDestroy {

  private dialogRef: MatDialogRef<TokenDialogComponent> | null = null;
  private expiryTimerId: ReturnType<typeof setTimeout> | null = null;
  private watchedToken: string | null = null;

  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
    private store: Store,
    private tokenRefresh: TokenRefreshService
  ) { }

  /**
   * Decodes a JWT to read its expiration.
   *
   * @param token The access token to decode.
   * @returns The expiration details, or null when the token is absent or unreadable.
   */
  getTokenExpirationInfo(token: string | null | undefined): TokenExpirationInfo | null {
    if (!token) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp; // Unix timestamp, in seconds
      const secondsRemaining = exp - Math.floor(Date.now() / 1000);

      return {
        expirationDate: new Date(exp * 1000),
        secondsRemaining,
        isExpired: secondsRemaining <= 0
      };
    } catch (e) {
      console.error('Invalid token:', e);
      return null;
    }
  }

  /** Whether the supplied token has lapsed. Tokens that cannot be decoded are left alone. */
  isTokenExpired(token: string | null | undefined): boolean {
    return this.getTokenExpirationInfo(token)?.isExpired ?? false;
  }

  /**
   * Tracks a token and renews it silently shortly before it expires.
   *
   * Called on every app-state emission, so re-watching the same token is a no-op.
   * Passing an empty token just stops the previous watch, which is what happens while
   * the dialog is up.
   *
   * @param token The session access token to watch.
   */
  watchToken(token: string | null | undefined): void {
    const nextToken = token ?? '';
    if (nextToken === this.watchedToken) {
      return;
    }

    this.watchedToken = nextToken;
    this.clearExpiryTimer();

    if (!nextToken) {
      return;
    }

    this.scheduleRefresh(nextToken);
  }

  /**
   * Shows the re-authorization dialog, clearing the stored token so nothing keeps
   * sending it. Repeat calls while the dialog is already up are ignored, so several
   * failing requests still produce a single prompt.
   */
  promptReauthentication(): void {
    if (this.dialogRef) {
      return;
    }

    this.clearExpiryTimer();
    this.watchedToken = '';
    this.store.dispatch(setAuthToken({ authToken: '' }));

    this.dialogRef = this.dialog.open(
      TokenDialogComponent,
      {
        autoFocus: false,
        scrollStrategy: this.overlay.scrollStrategies.noop(),
        disableClose: true,
        width: '300px',
        height: '300px'
      }
    );

    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.dialogRef = null;
    });
  }

  /** Whether the re-authorization dialog is currently open. */
  get isPrompting(): boolean {
    return !!this.dialogRef;
  }

  /**
   * Arms a timer to renew the token ahead of its expiry, re-arming itself for waits
   * longer than a timer can hold.
   */
  private scheduleRefresh(token: string): void {
    // Always cleared first: a renewal publishes the new token to the store, which calls
    // back into `watchToken` and arms a timer before `runScheduledRefresh` arms its own.
    this.clearExpiryTimer();

    const info = this.getTokenExpirationInfo(token);
    if (!info) {
      return;
    }

    if (info.isExpired) {
      // Try to recover silently before troubling the operator — an expired access token
      // is renewable as long as the refresh token behind it is still good.
      void this.runScheduledRefresh();
      return;
    }

    const delayMs = Math.max(info.secondsRemaining - REFRESH_SKEW_SECONDS, 0) * 1000;
    if (delayMs > MAX_TIMER_MS) {
      this.expiryTimerId = setTimeout(() => this.scheduleRefresh(token), MAX_TIMER_MS);
      return;
    }

    this.expiryTimerId = setTimeout(() => void this.runScheduledRefresh(), delayMs);
  }

  /** Renews the token silently, falling back to the dialog only when Azure AD insists. */
  private async runScheduledRefresh(): Promise<void> {
    if (this.dialogRef) {
      return;
    }

    const outcome = await this.tokenRefresh.acquire(true);

    switch (outcome.status) {
      case 'refreshed':
        this.watchedToken = outcome.accessToken;
        this.scheduleRefresh(outcome.accessToken);
        return;

      case 'interaction-required':
      case 'no-account':
        this.promptReauthentication();
        return;

      default:
        // Transient — a dropped network, not a rejected session. Keep trying while the
        // current token still has life in it.
        this.scheduleRetry();
    }
  }

  /** Re-arms a short retry, or gives up and prompts once the current token has lapsed. */
  private scheduleRetry(): void {
    const info = this.getTokenExpirationInfo(this.watchedToken);
    if (!info) {
      return;
    }

    if (info.isExpired) {
      this.promptReauthentication();
      return;
    }

    this.expiryTimerId = setTimeout(() => void this.runScheduledRefresh(), RETRY_DELAY_MS);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimerId !== null) {
      clearTimeout(this.expiryTimerId);
      this.expiryTimerId = null;
    }
  }

  ngOnDestroy(): void {
    this.clearExpiryTimer();
  }
}
